import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface SingleDatePickerProps {
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  error?: string;
  minDate?: Date;
  placeholder?: string;
}

const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  selectedDate,
  onDateChange,
  error,
  minDate = new Date(),
  placeholder = "mm/dd/yy",
}) => {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const formatDate = (date: Date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };

  const getDisplayText = () => {
    if (selectedDate) {
      return formatDate(selectedDate);
    }
    return placeholder;
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateSelected = (date: Date) => {
    return selectedDate && date.getTime() === selectedDate.getTime();
  };

  const isDateDisabled = (date: Date) => {
    return date < minDate;
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    onDateChange(date);
    setOpen(false);
  };

  const handleClear = () => {
    onDateChange(undefined);
    setOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    if (!isDateDisabled(today)) {
      onDateChange(today);
      setOpen(false);
    }
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      if (direction === "prev") {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const getMonthYearText = () => {
    const months = [
      "janvier",
      "février",
      "mars",
      "avril",
      "mai",
      "juin",
      "juillet",
      "août",
      "septembre",
      "octobre",
      "novembre",
      "décembre",
    ];
    return `${months[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={`w-full h-12 px-4 py-2  border-b border-b-gray-300 bg-transparent hover:border-b-[#87764e] focus:border-b-[#87764e] transition-colors cursor-pointer flex items-center justify-between ${
            error ? "border-red-500" : ""
          } ${open ? "border-b-[#87764e] ring-2 ring-[#87764e]/20" : ""}`}
        >
          <span className={`${selectedDate ? "text-gray-700" : "text-gray-400"}`}>
            {getDisplayText()}
          </span>
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 z-[999999] p-0 bg-white shadow-xl border border-gray-200 rounded-lg"
        align="start"
      >
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <button
            type="button"
            onClick={() => navigateMonth("prev")}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="flex items-center space-x-2">
            <span className="font-semibold text-gray-700">
              {getMonthYearText()}
            </span>
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          <button
            type="button"
            onClick={() => navigateMonth("next")}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-4">
          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-gray-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={index} className="h-8" />;
              }

              const isDisabled = isDateDisabled(date);
              const isSelected = isDateSelected(date);

              return (
                <button
                  type="button"
                  key={index}
                  className={`h-8 w-8 text-sm rounded-full flex items-center justify-center transition-colors ${
                    isDisabled
                      ? "text-gray-300 cursor-not-allowed"
                      : isSelected
                      ? "bg-[#87764e] text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => handleDateClick(date)}
                  disabled={isDisabled}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Calendar Footer */}
        <div className="flex justify-between p-4 border-t">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleToday}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Today
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SingleDatePicker;